import { useState } from 'react'
import { motion } from 'framer-motion'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfToday } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface DatePickerCalendarProps {
  value: Date
  onChange: (date: Date) => void
  onCancel?: () => void
  onSave?: () => void
  minDate?: Date
  className?: string
  title?: string
}

export function DatePickerCalendar({
  value,
  onChange,
  onCancel,
  onSave,
  minDate = startOfToday(),
  className,
  title
}: DatePickerCalendarProps) {
  const [currentDate, setCurrentDate] = useState(value || new Date())
  const [slideDirection, setSlideDirection] = useState(0)
  const [selectedDate, setSelectedDate] = useState(value)

  const handlePrevMonth = () => {
    setSlideDirection(-1)
    setCurrentDate(prev => subMonths(prev, 1))
  }

  const handleNextMonth = () => {
    setSlideDirection(1)
    setCurrentDate(prev => addMonths(prev, 1))
  }

  const handleDayClick = (date: Date) => {
    setSelectedDate(date)
    onChange(date)
  }

  const handleSave = () => {
    onSave?.()
  }

  const handleCancel = () => {
    onCancel?.()
  }

  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  // Get day names for the header (Mon-Sun)
  const dayNames = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(2024, 0, i + 1) // Using a Monday as reference
    return format(date, 'EEE')
  })

  // Calculate padding days for the start of the month
  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
  const paddingDays = Array.from({ length: startPadding }, () => null)

  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction * 50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction * -50,
      opacity: 0
    })
  }

  return (
    <div className={cn("bg-card rounded-lg p-2", className)}>
      {/* Title */}
      {title && (
        <div className="mb-2">
          <h3 className="text-xs font-medium text-foreground">{title}</h3>
        </div>
      )}
      
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-2">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={handlePrevMonth}
          className="p-1 rounded-full hover:bg-muted"
          type="button"
        >
          <ChevronLeft className="w-4 h-4" />
        </motion.button>
        
        <motion.h2 
          key={currentDate.toISOString()}
          custom={slideDirection}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="text-base font-medium"
        >
          {format(currentDate, 'MMMM yyyy')}
        </motion.h2>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleNextMonth}
          className="p-1 rounded-full hover:bg-muted"
          type="button"
        >
          <ChevronRight className="w-4 h-4" />
        </motion.button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {/* Day names */}
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
            {day}
          </div>
        ))}
        
        {/* Padding days */}
        {paddingDays.map((_, i) => (
          <div key={`pad-${i}`} className="aspect-square" />
        ))}
        
        {/* Actual days */}
        {daysInMonth.map(date => {
          const isSelected = isSameDay(date, selectedDate)
          const isPast = isBefore(date, minDate)
          
          return (
            <motion.button
              key={date.toISOString()}
              whileHover={{ scale: 0.95 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => !isPast && handleDayClick(date)}
              disabled={isPast}
              type="button"
              className={cn(
                "aspect-square rounded-md flex items-center justify-center text-xs transition-colors relative",
                isSelected && "bg-primary text-primary-foreground",
                !isSelected && !isPast && "hover:bg-muted",
                isToday(date) && !isSelected && "border border-primary",
                !isSameMonth(date, currentDate) && "text-muted-foreground opacity-50",
                isPast && "opacity-50 cursor-not-allowed"
              )}
            >
              {format(date, 'd')}
            </motion.button>
          )
        })}
      </div>

      {/* Action Buttons */}
      {(onCancel || onSave) && (
        <div className="grid grid-cols-2 gap-2 pt-2">
          {onCancel && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCancel}
              className="w-full"
              type="button"
            >
              Cancel
            </Button>
          )}
          {onSave && (
            <Button 
              size="sm" 
              onClick={handleSave}
              className="w-full"
              type="button"
            >
              Save
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

